import React, { useState } from "react";
import { BellSimple, EnvelopeSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { Switch } from "../../components/ui/switch";
import { toast } from "sonner";
import SettingsSection from "./SettingsSection";

const DEFAULTS = {
  email_enabled: true,
  ticket_created: true,
  status_changed: true,
  new_comment: true,
};

export default function NotificationsPanel({ user, setUser }) {
  const [prefs, setPrefs] = useState({ ...DEFAULTS, ...(user.notify_prefs || {}) });
  const [saving, setSaving] = useState(false);

  const EVENTS = [
    { key: "ticket_created", label: "New tickets", desc: user.role === "client_user" || user.role === "client_admin"
        ? "A confirmation when you submit a ticket." : "When a new ticket is created." },
    { key: "status_changed", label: "Status updates", desc: "When a ticket moves to a status your admin has marked for client email (e.g. completed)." },
    { key: "new_comment", label: "New comments", desc: "When someone comments on a ticket you're involved in." },
  ];

  const save = async (next) => {
    setSaving(true);
    setPrefs(next);
    try {
      const { data } = await api.patch("/me/notification-preferences", next);
      setUser((u) => ({ ...u, notify_prefs: data }));
      toast.success("Preferences saved");
    } catch (err) {
      setPrefs({ ...DEFAULTS, ...(user.notify_prefs || {}) }); // revert
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save");
    } finally { setSaving(false); }
  };

  const toggle = (key) => save({ ...prefs, [key]: !prefs[key] });
  const masterOff = !prefs.email_enabled;

  return (
    <SettingsSection title="Notifications" description="Choose which email updates you'd like to receive.">
      <div className="surface-card p-4 flex items-start gap-3">
        <div className="w-9 h-9 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><EnvelopeSimple size={18} /></div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-800">Email notifications</div>
          <div className="text-xs text-slate-500 mt-0.5">Master switch for all Support By ERP4U emails. Turn off to stop everything below.</div>
        </div>
        <Switch checked={!!prefs.email_enabled} disabled={saving} onCheckedChange={() => toggle("email_enabled")} data-testid="pref-email_enabled" />
      </div>

      <div className="surface-card divide-y divide-slate-100">
        <div className="px-4 py-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">
          <BellSimple size={13} /> Notify me about
        </div>
        {EVENTS.map((e) => (
          <div key={e.key} className={`p-4 flex items-start gap-3 ${masterOff ? "opacity-50" : ""}`}>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-800 text-sm">{e.label}</div>
              <div className="text-xs text-slate-500 mt-0.5">{e.desc}</div>
            </div>
            <Switch checked={!!prefs[e.key]} disabled={saving || masterOff} onCheckedChange={() => toggle(e.key)} data-testid={`pref-${e.key}`} />
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400">Signed in as {user.email}.</p>
    </SettingsSection>
  );
}
